import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./NewTickerTape.module.scss";

interface CssTickerProps {
    /** One run of the tape; it is repeated as often as it takes to fill the width. */
    renderRow: (copy: number) => ReactNode;
    move: boolean;
    /** Pixels per second (react-ticker's speed 3 was 30 px/s). */
    speed?: number;
}

/**
 * A right-to-left tape driven by a CSS animation, which the compositor runs off the main thread.
 * react-ticker moved its element from a requestAnimationFrame loop instead: a style write and a
 * layout read every frame, on every page, for as long as the tab was open, which kept a phone's
 * main thread busy and delayed whatever else wanted to run (input, the consent notice, page data).
 *
 * The first copy of the row is measured once (and again when its width changes, e.g. a price gets
 * longer); the track holds enough copies to cover the viewport plus one, and the animation moves it
 * by exactly one row width, so the loop is seamless. A new width changes the animation's duration,
 * which would make the browser re-place the tape from the new duration (a visible jump), so the
 * offset into the current loop is carried over to the new animation.
 */
export default function CssTicker({ renderRow, move, speed = 30 }: CssTickerProps) {
    const viewportRef = useRef<HTMLDivElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const [rowWidth, setRowWidth] = useState(0);
    const [copies, setCopies] = useState(2);
    const trackRef = useRef<HTMLDivElement>(null);
    const widthRef = useRef(0);
    // Milliseconds into the current loop when the width changed; restored once the new width applies.
    const carryRef = useRef<number | null>(null);

    useEffect(() => {
        const row = rowRef.current;
        const viewport = viewportRef.current;
        if (!row || !viewport || typeof ResizeObserver === "undefined") return;
        const measure = () => {
            const w = row.offsetWidth;
            if (w !== widthRef.current && widthRef.current > 0) {
                const t = trackRef.current?.getAnimations?.()[0]?.currentTime;
                // At a constant speed the tape is (time into the loop x speed) px along, whatever the width.
                if (typeof t === "number") carryRef.current = t % ((widthRef.current / speed) * 1000);
            }
            widthRef.current = w;
            setRowWidth(w);
            setCopies(w > 0 ? Math.ceil(viewport.offsetWidth / w) + 1 : 2);
        };
        const ro = new ResizeObserver(measure);
        ro.observe(row);
        ro.observe(viewport);
        return () => ro.disconnect();
    }, [speed]);

    useLayoutEffect(() => {
        if (carryRef.current === null) return;
        const animation = trackRef.current?.getAnimations?.()[0];
        if (animation) animation.currentTime = carryRef.current;
        carryRef.current = null;
    }, [rowWidth]);

    const style = rowWidth
        ? ({
              "--ticker-shift": `-${rowWidth}px`,
              animationDuration: `${rowWidth / speed}s`,
              animationPlayState: move ? "running" : "paused",
          } as React.CSSProperties)
        : undefined;

    return (
        <div ref={viewportRef} className={styles.cssTickerViewport}>
            <div ref={trackRef} className={rowWidth ? styles.cssTickerTrack : styles.cssTickerTrackIdle} style={style}>
                {Array.from({ length: copies }, (_, i) => (
                    <div key={i} ref={i === 0 ? rowRef : undefined} className={styles.cssTickerRow} aria-hidden={i > 0 ? true : undefined}>
                        {renderRow(i)}
                    </div>
                ))}
            </div>
        </div>
    );
}
